import {Color} from '@benchling/nucleus-ui';
import {createStyles, Popover, Text} from '@mantine/core';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {AGENT_MODES_LEARN_MORE_URL} from 'llm/NamingConstants';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';

const useStyles = createStyles({
  link: {
    color: Color.BLUE_450,
    textDecoration: 'underline',
  },
});

/** Delay before closing on mouse-out, so moving the pointer from the target to the dropdown (or
 * vice versa) doesn't close the popover in the gap between them. */
const CLOSE_DELAY_MS = 100;

export interface FullActionsInfoPopoverProps {
  /** The element that opens the popover on hover/focus. Must forward a ref (e.g. a DOM element). */
  children: React.ReactElement;
}

/**
 * Hover/focus-triggered popover explaining Full actions mode, with a clickable "Learn more" link.
 *
 * Mantine's `Tooltip` only tracks hover on its target, not on its own floating content, so a link
 * inside a `Tooltip` label closes the tooltip the moment the pointer leaves the target to reach it
 * -- making the link unusable by mouse. `Popover` lets us track hover on the dropdown too, so the
 * popover (and its link) stays open while the pointer is over either the target or the dropdown.
 */
export default function FullActionsInfoPopover({children}: FullActionsInfoPopoverProps): JSX.Element {
  const {classes} = useStyles();
  const [opened, setOpened] = useState(false);
  const closeTimeoutRef = useRef<number | null>(null);

  const cancelPendingClose = useCallback((): void => {
    if (closeTimeoutRef.current != null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const handleOpen = useCallback((): void => {
    cancelPendingClose();
    setOpened(true);
  }, [cancelPendingClose]);

  const handleClose = useCallback((): void => {
    cancelPendingClose();
    closeTimeoutRef.current = window.setTimeout(() => {
      setOpened(false);
    }, CLOSE_DELAY_MS);
  }, [cancelPendingClose]);

  useEffect(() => cancelPendingClose, [cancelPendingClose]);

  return (
    <Popover
      opened={opened}
      position="top"
      withArrow={true}
      withinPortal={true}
      width={260}
      shadow="md"
      zIndex={ZINDEX_FLOATING_UI}
      // Disable the open/close animation: it isn't worth the complexity of coordinating with our
      // own close-delay timer above, and an instant toggle is unnoticeable for a small info popover.
      transitionProps={{duration: 0}}
    >
      <Popover.Target>
        <span onMouseEnter={handleOpen} onMouseLeave={handleClose} onFocus={handleOpen} onBlur={handleClose}>
          {children}
        </span>
      </Popover.Target>
      <Popover.Dropdown onMouseEnter={handleOpen} onMouseLeave={handleClose}>
        <Text size="sm">
          In Full actions mode, create and edit Benchling objects within existing user permissions.{' '}
          <a
            href={AGENT_MODES_LEARN_MORE_URL}
            target="_blank"
            rel="noreferrer"
            className={classes.link}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            Learn more
          </a>
        </Text>
      </Popover.Dropdown>
    </Popover>
  );
}
